import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mfaApi, authApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { redirectByRole } from './Login';

export default function MfaVerify() {
  const navigate           = useNavigate();
  const location           = useLocation();
  const { refreshUser }    = useAuth();

  const tempToken          = location.state?.tempToken;
  const [code, setCode]    = useState(['', '', '', '', '', '']);
  const [error, setError]  = useState('');
  const [loading, setLoad] = useState(false);
  const [token, setToken]  = useState(tempToken);
  const inputRefs          = useRef([]);

  useEffect(() => {
    if (!tempToken) navigate('/login');
    inputRefs.current[0]?.focus();
  }, [tempToken, navigate]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[i] = val;
    setCode(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
    if (e.key === 'Enter') handleSubmit();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setCode(text.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) { setError('Enter all 6 digits'); return; }
    setLoad(true); setError('');
    try {
      await mfaApi.verify(token, parseInt(fullCode));
      // Session is now created — fetch user
      const meRes = await authApi.me();
      const user  = meRes.data?.data;
      if (!user) throw new Error('Session error');
      await refreshUser();
      if (user.forceReset) navigate('/reset-password');
      else redirectByRole(user.role, navigate);
    } catch (err) {
      const raw = err.response?.data?.message || err.message || 'Verification failed';
      if (raw.startsWith('INVALID_CODE:')) {
        setToken(raw.split(':')[1]);
        setError('Incorrect code. Please try again.');
      } else {
        setError(raw);
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoad(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.iconWrap}>🔐</div>
        <h1 style={S.title}>Two-Factor Verification</h1>
        <p style={S.sub}>
          Open your authenticator app and enter the 6-digit code for <strong>Tracern</strong>.
        </p>

        {error && <div style={S.err}>{error}</div>}

        <div style={S.boxes} onPaste={handlePaste}>
          {code.map((d, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              style={{
                ...S.box,
                borderColor: d ? 'var(--color-primary)' : 'var(--color-border)',
                background: d ? 'var(--color-primary-faint)' : '#fff',
              }}
            />
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || code.join('').length !== 6}
          style={{
            ...S.btn,
            opacity: loading || code.join('').length !== 6 ? 0.5 : 1,
          }}
        >
          {loading ? 'Verifying…' : 'Verify →'}
        </button>

        <button onClick={() => navigate('/login')} style={S.back}>
          ← Back to login
        </button>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--color-bg)', padding: '24px',
  },
  card: {
    background: '#fff', borderRadius: '20px', padding: '40px 36px',
    maxWidth: '420px', width: '100%', textAlign: 'center',
    boxShadow: '0 8px 40px rgba(0,0,0,.08), 0 2px 8px rgba(0,0,0,.04)',
    border: '1px solid var(--color-border)',
  },
  iconWrap: { fontSize: '40px', marginBottom: '16px' },
  title: { fontSize: '22px', fontWeight: 800, color: 'var(--color-text)', marginBottom: '10px', letterSpacing: '-0.02em' },
  sub: { fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.65, marginBottom: '24px' },
  err: { padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', color: '#DC2626', fontSize: '13px', marginBottom: '20px', textAlign: 'left' },
  boxes: { display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '24px' },
  box: {
    width: '48px', height: '56px', borderRadius: '12px', border: '1.5px solid',
    textAlign: 'center', fontSize: '22px', fontWeight: 800, color: 'var(--color-text)',
    outline: 'none', transition: 'all .15s',
    fontFamily: 'var(--font-mono, monospace)',
  },
  btn: {
    width: '100%', padding: '14px', borderRadius: '12px',
    background: 'var(--color-primary)', color: '#fff',
    fontSize: '15px', fontWeight: 700, border: 'none', cursor: 'pointer',
    transition: 'opacity .15s', marginBottom: '14px',
  },
  back: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', color: 'var(--color-text-muted)', padding: '4px',
  },
};
